import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, Phone, Mail, MapPin, BookUser } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { Toaster } from '../components/ui/toaster';

const SORT = [['name', 'Name A–Z'], ['akten', 'Meiste Akten'], ['ort', 'Ort'], ['zuletzt', 'Zuletzt verwendet']];
const chip = (an) => `h-8 px-3 rounded-full text-xs border transition-colors ${an ? 'bg-[#1B3A5C] border-[#1B3A5C] text-white' : 'bg-white border-[#DCE3EA] text-[#1B3A5C] hover:border-[#b8893d]'}`;

const Kontakt = ({ k, i }) => (
  <li className="rounded-2xl border border-[#1B3A5C]/10 bg-white p-4 space-y-3" data-testid={`kv-kontakt-${i}`}>
    <div className="flex flex-wrap items-start gap-3">
      <div className="flex-1 min-w-[14rem]">
        <p className="text-[10px] tracking-[0.15em] uppercase text-[#b8893d]">{k.rolle_label} · {k.anzahl_akten} Akte{k.anzahl_akten === 1 ? '' : 'n'}</p>
        <p className="text-base font-medium text-[#15202B] break-words" data-testid={`kv-name-${i}`}>{k.name}</p>
        {k.ansprechpartner && <p className="text-xs text-[#56616D]">{k.ansprechpartner}</p>}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#34536b]">
        {k.telefon && <a href={`tel:${k.telefon}`} className="inline-flex items-center gap-1 hover:text-[#b8893d]"><Phone className="w-3 h-3" />{k.telefon}</a>}
        {k.email && <a href={`mailto:${k.email}`} className="inline-flex items-center gap-1 hover:text-[#b8893d]"><Mail className="w-3 h-3" />{k.email}</a>}
        {(k.strasse || k.ort) && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{[k.strasse, [k.plz, k.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}</span>}
      </div>
    </div>
    <ul className="flex flex-wrap gap-1.5" data-testid={`kv-akten-${i}`}>
      {k.akten.map((a) => (
        <li key={a.id}><Link to={`/kanzlei/akten/${a.case_id}?reiter=beteiligte`} className="inline-flex flex-wrap items-center gap-x-1.5 min-h-8 py-1 px-3 rounded-lg bg-[#f5f8fa] border border-transparent text-xs text-[#1B3A5C] hover:border-[#b8893d]" data-testid={`kv-akte-${a.case_id}`}>
          <span className="font-mono whitespace-nowrap">{a.aktenzeichen || 'ohne AZ'}</span><span className="text-[#56616D] truncate max-w-[14rem]">{[a.mandant, a.gegner].filter(Boolean).join(' ./. ')}</span>{a.aktenzeichen_beteiligter && <span className="text-[#7a8a99]">· {a.aktenzeichen_beteiligter}</span>}
        </Link></li>))}
    </ul>
  </li>
);

// Kontaktverzeichnis: Gerichte, Gegenanwälte, Versicherungen, Behörden, Sachverständige über alle Akten
export default function Kontaktverzeichnis() {
  const [q, setQ] = useState('');
  const [rolle, setRolle] = useState('');
  const [sort, setSort] = useState('name');
  const [d, setD] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get('/kanzlei/kontakte', { params: { q, rolle, sort } }).then((r) => setD(r.data)).catch((x) => setD({ items: [], rollen: [], fehler: x?.response?.data?.detail || 'Kontakte konnten nicht geladen werden' })), 250);
    return () => clearTimeout(t);
  }, [q, rolle, sort]);
  return (
    <div className="min-h-screen bg-[#f5f8fa]" data-testid="kontaktverzeichnis">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] pb-20">
        <div className="max-w-[1500px] mx-auto px-4 lg:px-8 pt-[22px] space-y-4">
          <div className="flex items-center gap-3"><BookUser className="w-6 h-6 text-[#b8893d]" /><h1 className="font-serif text-3xl text-[#15202B]">Kontaktverzeichnis</h1></div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-xl border border-[#1B3A5C]/12 bg-white px-3 h-10 w-full sm:w-96 focus-within:border-[#b8893d]">
              <Search className="w-4 h-4 text-[#7a8a99]" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, Ort, Telefon, E-Mail, Aktenzeichen" className="flex-1 min-w-0 text-sm bg-transparent focus:outline-none" data-testid="kv-suche" />
            </div>
            <button type="button" onClick={() => setRolle('')} className={chip(!rolle)} data-testid="kv-rolle-alle">Alle</button>
            {(d?.rollen || []).map((r) => <button key={r.key} type="button" onClick={() => setRolle(r.key)} className={chip(rolle === r.key)} data-testid={`kv-rolle-${r.key}`}>{r.label}</button>)}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-[#7a8a99]">Sortieren:
            {SORT.map(([k, l]) => <button key={k} type="button" onClick={() => setSort(k)} className={chip(sort === k)} data-testid={`kv-sort-${k}`}>{l}</button>)}
            {d && <span className="ml-auto" data-testid="kv-anzahl">{d.items.length} Kontakt{d.items.length === 1 ? '' : 'e'}</span>}
          </div>
          {!d ? <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" /></div>
            : d.fehler ? <p className="text-sm text-rose-700 text-center py-12" data-testid="kv-fehler">{d.fehler}</p>
            : !d.items.length ? <p className="text-sm text-[#7a8a99] text-center py-12" data-testid="kv-leer">Keine Kontakte gefunden. Kontakte entstehen automatisch, wenn Sie in einer Akte unter „Beteiligte“ Gerichte, Gegenanwälte oder Versicherungen anlegen.</p>
            : <ul className="grid xl:grid-cols-2 gap-3" data-testid="kv-liste">{d.items.map((k, i) => <Kontakt key={`${k.rolle}-${k.name}`} k={k} i={i} />)}</ul>}
        </div>
      </main>
      <Toaster />
    </div>
  );
}
